import React from 'react';
import Icon from '@src/v3/components/coreUI/icon';

/**
 * IconSection - A reusable component for displaying icons in a styled container
 *
 * @param iconName - The name of the icon from the iconList (e.g., 'call', 'email', 'tickCircleFill')
 * @param iconClassName - Custom CSS classes for the icon (default: 'w-14 h-14 text-white')
 * @param containerClassName - Custom CSS classes for the outer container
 * @param innerContainerClassName - Custom CSS classes for the inner container
 *
 * @example
 * // Basic usage with default styling
 * <IconSection iconName="callWhite" />
 *
 * // Custom icon styling
 * <IconSection iconName="email" iconClassName="w-16 h-16 text-blue-600" />
 *
 * // Custom container styling
 * <IconSection
 *   iconName="tickCircleFill"
 *   iconClassName="w-14 h-14 text-green-600"
 *   containerClassName="mt-4"
 * />
 */
interface IconSectionProps {
  iconName: string;
  iconClassName?: string;
  containerClassName?: string;
  innerContainerClassName?: string;
  innerShadowContainerClassName?: string;
}

const IconSection: React.FC<IconSectionProps> = ({
  iconName,
  iconClassName = 'w-14 h-14 text-white',
  containerClassName = '',
  innerContainerClassName = '',
  innerShadowContainerClassName = '',
}) => {
  return (
    <div
      className={`pt-3 flex justify-center overflow-hidden ${containerClassName}`}
    >
      <div
        className={`flex border rounded-br-none rounded-bl-none border-white items-center justify-center w-96 h-32 rounded-[52px] bg-white shadow-[-10px_-8px_20px_-3px_rgba(0,108,255,0.10),10px_10px_20px_-3px_rgba(247,31,248,0.10)] ${innerContainerClassName}`}
      >
        <div
          className={`flex items-self-center border rounded-br-none rounded-bl-none border-white items-center justify-center w-full h-full mx-5 mt-10 rounded-[46px] bg-white shadow-[81px_281px_82px_0_rgba(0,0,0,0),52px_180px_75px_0_rgba(0,0,0,0.02),29px_101px_63px_0_rgba(0,0,0,0.07),13px_45px_47px_0_rgba(0,0,0,0.12),3px_11px_26px_0_rgba(0,0,0,0.14)] p-10 ${innerShadowContainerClassName}`}
        >
          <Icon iconName={iconName} iconProps={{ className: iconClassName }} />
        </div>
      </div>
    </div>
  );
};

export default IconSection;
